import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, FolderArchive, UserCog, ArrowLeft } from "lucide-react";
import NavBar from "@/library/components/NavBar";
import Footer from "@/library/components/Footer";

export default function LibraryHub() {
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 pt-10 sm:pt-14 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/5 text-brand text-xs font-heading">
            <LayoutGrid className="w-3.5 h-3.5" /> المكتبة الرقمية — اختر القسم
          </div>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-black text-brand-dark tracking-tight">
            بوابة المكتبة الرقمية
          </h1>
          <div className="gold-line mx-auto mt-3" />
          <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">
            المكتبة الرقمية تحتوي على قسمين مستقلين — كل قسم بلوحته الخاصة، محتواه، وصلاحياته.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-7">
          <HubCard
            to="/library/home"
            testid="hub-card-school-docs"
            accent="brand"
            icon={FolderArchive}
            title="ملفات وثائق المدرسة"
            desc="المكتبة الرقمية الرئيسية — أرشيف الوثائق التعليمية بتنظيم هرمي: المرحلة ← المجال ← المؤشر ← الملف."
            tags={["أرشفة", "PDF", "المؤشرات", "المراحل"]}
            cta="استعراض المكتبة"
            delay={0.05}
          />
          <HubCard
            to="/library/staff/browse"
            testid="hub-card-staff-docs"
            accent="gold"
            icon={UserCog}
            title="ملفات وثائق منسوبي المدرسة"
            desc="منصة خاصة بمنسوبي المدرسة لرفع وتنظيم وثائقهم — تصفح مباشرة أو سجّل الدخول لإدارة ملفاتك."
            tags={["الموظفين", "المجلدات", "الوثائق", "خاص"]}
            cta="استعراض / الدخول"
            delay={0.15}
          />
        </div>

        <div className="mt-8 text-center">
          <Link to="/library/staff/login" data-testid="hub-staff-login-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-brand-dark font-heading">
            هل أنت من منسوبي المدرسة؟ <span className="text-gold-dark font-bold">تسجيل الدخول لإدارة ملفاتك</span>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function HubCard({ to, testid, accent, icon: Icon, title, desc, tags, cta, delay }) {
  const isBrand = accent === "brand";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <Link
        to={to}
        data-testid={testid}
        className="group relative block rounded-3xl overflow-hidden bg-white ring-1 ring-slate-200/80 shadow-[0_8px_28px_-15px_rgba(13,115,119,0.25)] hover:shadow-[0_18px_45px_-15px_rgba(13,115,119,0.35)] transition-all duration-500 hover:-translate-y-1"
      >
        <div className={`h-1.5 w-full ${isBrand ? "bg-gradient-to-l from-brand-dark via-brand to-brand-light" : "bg-gradient-to-l from-gold-dark via-gold to-gold-light"}`} />
        <div className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center ring-1 ${isBrand ? "bg-brand/5 text-brand ring-brand/20" : "bg-gold/10 text-gold-dark ring-gold/30"} group-hover:scale-105 transition-transform`}>
              <Icon className="w-7 h-7" strokeWidth={2.1} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-extrabold text-brand-dark text-xl sm:text-2xl tracking-tight">{title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{desc}</p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className={`text-[11px] px-2 py-0.5 rounded-full ${isBrand ? "bg-brand/5 text-brand border border-brand/15" : "bg-gold/10 text-gold-dark border border-gold/25"}`}>
                {t}
              </span>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <span className={`inline-flex items-center gap-2 font-heading font-bold text-sm px-4 py-2 rounded-xl transition-colors ${isBrand ? "bg-brand text-white group-hover:bg-brand-light" : "bg-gold text-white group-hover:bg-gold-light"}`}>
              {cta}
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
